import { createI18n } from 'vue-i18n'
import _accounting from 'accounting'
import { ACCOUNTING_SETTINGS, LANGUAGES } from './lib/consts.js'
import { getCurrentLocale, LOCALE_KEY } from '@/lib/locale.js'

import { country, language, baseUrl, basePath } from '@/router/location-params.js'

// fallback (en) language messages
import fallbackMessages from '@/messages/en.js'

import { getMomentFormats } from '@/lib/moment-formats.js'
import moment from 'moment'

// moment and accounting formats for current country & language
export const MOMENT_FORMATS = {}
export const accounting = _accounting
/*
 *
 * setup & modules/components/... related to country & language
 *
 */
export async function localize () {
  return new Promise((resolve, reject) => {
    /*
     * we need to delay i18n initialization, till VueI18n plugin is
     * registered; see: `@/main.js` - method `localize` is called
     * after `registerPlugins`
     */
    const i18n = createI18n({
      legacy: true,
      locale: 'en',
      fallbackLocale: 'en',
      messages: {
        en: sanitizeMessages(fallbackMessages),
      },
      pluralizationRules: {
        pl: pluralizationPL,
        en: pluralizationEN,
        'en-gb': pluralizationEN,
        pt: pluralizationPT,
        es: pluralizationES,
        'es-es': pluralizationES,
        uk: pluralizationUK,
      },
    })

    const originalT = i18n.global.t

    // HACK-VUE3 - we get error "Invalid arguments" when we call `i18n.global.t` with null or undefined
    // there may be many such places in the project at the moment....
    i18n.global.t = (key) => {
      if (key) return originalT.call(i18n, key)
      return ''
    }

    // if unsupported languaged - redirect to en language
    const supportedLanguages = LANGUAGES.map(l => l.code)

    const locale = getCurrentLocale(language, country)

    if (!supportedLanguages.includes(locale)) {
      const a = `/${language}-${country}/`
      const b = `/en-${country}/`

      localStorage.removeItem(LOCALE_KEY)

      const url = new URL(window.location.href.replace(a, b))
      url.searchParams.delete('locale')
      window.location.href = url.toString()
      return reject(new Error('Unsupported language'))
    }

    localeMoment(locale)

    // load translation messages for current language & country
    loadMessages(i18n, language, locale).then(() => {
      // if moment is set by language, format should be too
      Object.assign(MOMENT_FORMATS, getMomentFormats(country, i18n))
      accounting.settings = ACCOUNTING_SETTINGS[country]
      resolve({ country, language, locale, i18n, baseUrl, basePath })
    })
  })
}

function loadMessages (i18n, language, locale) {
  // shortcut: language is same as default
  if (i18n.global.locale === locale) {
    return Promise.resolve(setI18nLanguage(i18n, locale, language))
  }

  // workaround for error, caused by: `@/messages/${lang}.js`
  return import(`./messages/${locale}.json`).then(messages => {
    i18n.global.setLocaleMessage(locale, sanitizeMessages(messages.default))
    return setI18nLanguage(i18n, locale, language)
  })
}

// HACK-VUE3
function sanitizeMessages (messages) {
  const sanitized = {}
  for (const [key, value] of Object.entries(messages)) {
    if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeMessages(value)
    } else if (typeof value === 'string') {
      sanitized[key] = value.replace(/@/g, match => `{'${match}'}`)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function setI18nLanguage (i18n, locale, language) {
  i18n.global.locale = locale
  document.querySelector('html').setAttribute('lang', language)
}

// we use our own translations for CKEditor to have them optimal and more configurable

const MOMENT_LOCALES = {
  en: () => import('moment/dist/locale/en-gb'),
  'en-gb': () => import('moment/dist/locale/en-gb'),
  pl: () => import('moment/dist/locale/pl'),
  es: () => import('moment/dist/locale/es'),
  'es-es': () => import('moment/dist/locale/es'),
  'es-mx': () => import('moment/dist/locale/es-mx'),
  pt: () => import('moment/dist/locale/pt'),
  'pt-br': () => import('moment/dist/locale/pt-br'),
  fr: () => import('moment/dist/locale/fr'),
  vi: () => import('moment/dist/locale/vi'),
  uk: () => import('moment/dist/locale/uk'),
  zh: () => import('moment/dist/locale/zh-cn'),
  'zh-cn': () => import('moment/dist/locale/zh-cn'),
}

async function localeMoment (locale) {
  try {
    await MOMENT_LOCALES[locale]()
    moment.locale(locale)
  } catch (e) {
    console.error(`moment-locale: failed to load ${locale}, back to en`)
  }
}

function pluralizationPL (n) {
  if (n === 0) return 2 // genitive
  else if (n === 1) return 0 // singular
  else if (n % 10 < 5) return 1 // genitive
  return 2 // plural
}

function pluralizationEN (n) {
  if (n === 1) return 0 // singular
  return 1 // plural
}

function pluralizationPT (n) {
  if (n === 0 || n === 1) return 0 // singular
  return 1 // plural
}

function pluralizationES (n) {
  if (n === 1) return 0 // singular
  return 1 // plural
}

function pluralizationUK (n) {
  if (n === 0) return 2 // genitive plural
  else if (n === 1) return 0 // singular
  else if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return 1 // few
  return 2 // many
}
